import { UsersPage } from '@/pages/Users';
import { useEffect, useState } from 'react';
import {
  Outlet,
  redirect,
  RootRoute,
  Route,
  Router,
  createMemoryHistory,
  createBrowserHistory,
} from '@tanstack/react-router';
import { AppShell } from '@/components/AppShell';
import { CommandPalette } from '@/components/CommandPalette';
import { Sidebar } from '@/components/Sidebar';
import { Topbar } from '@/components/Topbar';
import { AdminPage } from '@/pages/Admin';
import { DossiersPage } from '@/pages/Dossiers';
import { DossierPage } from '@/pages/Dossier';
import { ImportsPage } from '@/pages/Imports';
import { LoginPage } from '@/pages/Login';
import { PartnersPage } from '@/pages/Partners';
import { SettingsPage } from '@/pages/Settings';
import { useAuth } from '@/store/auth';

/**
 * Code-based TanStack Router. The four module routes + /settings share
 * an `_auth` layout that guards access and renders the sidebar + topbar
 * around the page Outlet. The command palette is mounted once at the
 * layout so it survives navigation and is reachable by Cmd/Ctrl+K from
 * any module.
 */

function requireAuth() {
  const { accessToken, refreshToken } = useAuth.getState();
  if (!accessToken && !refreshToken) {
    throw redirect({ to: '/login' });
  }
}

const rootRoute = new RootRoute({
  component: () => <Outlet />,
});

const loginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    const { accessToken, refreshToken } = useAuth.getState();
    if (accessToken || refreshToken) {
      throw redirect({ to: '/dossiers' });
    }
  },
  component: LoginPage,
});

const authLayoutRoute = new Route({
  getParentRoute: () => rootRoute,
  id: '_auth',
  beforeLoad: requireAuth,
  component: AuthLayout,
});

function AuthLayout() {
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Cmd/Ctrl + K global shortcut. We also intercept Cmd/Ctrl + F (the
  // mark MAFA used historically) so the operator can keep their muscle
  // memory.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isModifier = e.metaKey || e.ctrlKey;
      if (isModifier && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <AppShell
      sidebar={<Sidebar />}
      topbar={<Topbar crumbRoot="PDE.CI" onSearchClick={() => setPaletteOpen(true)} />}
    >
      <Outlet />
      <CommandPalette opened={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </AppShell>
  );
}

const indexRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dossiers' });
  },
  component: () => null,
});

const dossiersRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/dossiers',
  component: DossiersPage,
});

const dossierRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/dossiers/$phone',
  component: DossierPage,
});

const importsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/imports',
  component: ImportsPage,
});

const partnersRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/partners',
  component: PartnersPage,
});

const usersRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/users',
  component: UsersPage,
});

const adminRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/admin',
  component: AdminPage,
});

const settingsRoute = new Route({
  getParentRoute: () => authLayoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authLayoutRoute.addChildren([
    indexRoute,
    dossiersRoute,
    dossierRoute,
    importsRoute,
    partnersRoute,
    usersRoute,
    adminRoute,
    settingsRoute,
  ]),
]);

export const router = new Router({
  routeTree,
  history: typeof window === 'undefined' ? createMemoryHistory() : createBrowserHistory(),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
